import React from 'react';

import { INTAKE_FORM_HELP_TEXT_MAX_LENGTH, INTAKE_FORM_TEXT_MAX_LENGTH } from '@aline/common';
import { Typography } from '@mui/material';
import { CirclePlus, Trash2, X } from 'lucide-react';
import { AlineInput } from 'src/AlineInput';
import { SmallAlineButton } from 'src/components/generic/SmallAlineButton';
import { ToggleSwitch } from 'src/components/generic/ToggleSwitch';
import { makeStyles } from 'tss-react/mui';

import type { BuilderFieldType, DraftIntakeField, DraftSelectOption } from './intake-form-fields';
import {
  BUILDER_FIELD_META,
  getBuilderFieldNeedsOptions,
  getDraftFieldIsComplete,
} from './intake-form-fields';

const LABEL_PLACEHOLDERS: Record<BuilderFieldType, string> = {
  checkbox: 'Checkbox label',
  checklist: 'Checklist label',
  date: 'Date label',
  email: 'Email label',
  fileUpload: 'File request label',
  number: 'Number label',
  select: 'Select label',
  text: 'Field label',
  textarea: 'Field label',
};

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  addOption: {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: theme.appColors.primary,
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(0.5),
  },
  bottomRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  helpTextInput: {
    width: '100%',
  },
  iconBox: {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(0.5),
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  labelInput: {
    /**
     * The type icon is a start adornment on the InputBase root, which has no
     * left padding of its own — pad it so the icon doesn't touch the border.
     */
    '& .MuiInputBase-adornedStart': {
      paddingLeft: theme.spacing(1),
    },
    flex: 1,
    minWidth: 0,
  },
  optionRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  required: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    fontSize: 14,
    gap: theme.spacing(1),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  sectionLabel: {
    ...theme.appTypography.fieldLabel,
  },
  topRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface IntakeFormFieldEditorProps {
  draft: DraftIntakeField;
  onCancel: () => void;
  onChange: (draft: DraftIntakeField) => void;
  onDelete: () => void;
  onSave: () => void;
}

export function IntakeFormFieldEditor({
  draft,
  onCancel,
  onChange,
  onDelete,
  onSave,
}: IntakeFormFieldEditorProps) {
  const { classes } = useStyles();
  const Icon = BUILDER_FIELD_META[draft.type].icon;

  const updateOption = (index: number, option: DraftSelectOption) =>
    onChange({ ...draft, options: draft.options.map((o, i) => (i === index ? option : o)) });

  const needsOptions = getBuilderFieldNeedsOptions(draft.type);
  /**
   * Gate Save so a committed field always builds a valid definition (autosave
   * persists on commit): a label, and for option fields at least one option.
   */
  const canSave = getDraftFieldIsComplete(draft);

  return (
    <div className={classes.root}>
      <div className={classes.inputGroup}>
        <div className={classes.sectionLabel}>Title</div>
        <div className={classes.topRow}>
          <AlineInput
            /**
             * Focus the label as soon as the editor opens (e.g. right after picking
             * a field type) so the user can type immediately. The editor remounts
             * per field, so autoFocus fires each time it opens.
             */
            autoFocus
            className={classes.labelInput}
            inputProps={{ 'aria-label': 'Field label', maxLength: INTAKE_FORM_TEXT_MAX_LENGTH }}
            placeholder={LABEL_PLACEHOLDERS[draft.type]}
            startAdornment={<Icon color="#374951" size={18} />}
            value={draft.label}
            onChange={event => onChange({ ...draft, label: event.target.value })}
          />
          <button
            aria-label="Delete field"
            className={classes.iconBox}
            type="button"
            onClick={onDelete}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className={classes.inputGroup}>
        <div className={classes.sectionLabel}>Helper Text</div>
        <AlineInput
          multiline
          className={classes.helpTextInput}
          inputProps={{
            'aria-label': 'Field helper text',
            maxLength: INTAKE_FORM_HELP_TEXT_MAX_LENGTH,
          }}
          minRows={2}
          placeholder="Add helper text..."
          value={draft.helpText ?? ''}
          onChange={event => onChange({ ...draft, helpText: event.target.value })}
        />
      </div>

      {needsOptions && (
        <div className={classes.inputGroup}>
          <div className={classes.sectionLabel}>Options</div>
          <div className={classes.options}>
            {draft.options.map((option, index) => (
              <div key={index} className={classes.optionRow}>
                <AlineInput
                  inputProps={{ maxLength: INTAKE_FORM_TEXT_MAX_LENGTH }}
                  placeholder={`Option ${String(index + 1).padStart(2, '0')}`}
                  value={option.label}
                  onChange={event => updateOption(index, { ...option, label: event.target.value })}
                />
                {draft.options.length > 1 && (
                  <button
                    aria-label="Remove option"
                    className={classes.iconBox}
                    type="button"
                    onClick={() =>
                      onChange({ ...draft, options: draft.options.filter((_, i) => i !== index) })
                    }
                  >
                    <X size={16} />
                  </button>
                )}
                {index === draft.options.length - 1 && (
                  <button
                    aria-label="Add option"
                    className={classes.addOption}
                    type="button"
                    onClick={() =>
                      onChange({ ...draft, options: [...draft.options, { label: '', value: '' }] })
                    }
                  >
                    <CirclePlus size={20} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={classes.bottomRow}>
        <div className={classes.required}>
          <ToggleSwitch
            aria-checked={draft.required}
            aria-label="Required"
            role="switch"
            tabIndex={0}
            toggle={() => onChange({ ...draft, required: !draft.required })}
            toggled={draft.required}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onChange({ ...draft, required: !draft.required });
              }
            }}
          />
          <Typography>Required</Typography>
        </div>
        <div className={classes.actions}>
          <SmallAlineButton color="primary" variant="outlined" onClick={onCancel}>
            Cancel
          </SmallAlineButton>
          <SmallAlineButton
            color="primary"
            disabled={!canSave}
            variant="contained"
            onClick={onSave}
          >
            Save
          </SmallAlineButton>
        </div>
      </div>
    </div>
  );
}
